import { useMemo, useState } from "react";
import Breadcrumb from "./Breadcrumb";
import FilterSidebar, { type FilterGroupConfig } from "./FilterSidebar";
import ContentCard from "./ContentCard";
import Pagination from "./Pagination";
import EmptyState from "./EmptyState";
import type { ContentItem } from "@/types/content";

interface CategoryBrowserProps {
  heading: string;
  description: string;
  crumbLabel: string;
  groups: FilterGroupConfig[];
  initialValues: Record<string, string>;
  filter: (values: Record<string, string>) => ContentItem[];
  emptyText?: string;
  cardLayout?: "poster" | "wide";
  gridClassName?: string;
  pageSize?: number;
}

export default function CategoryBrowser({
  heading,
  description,
  crumbLabel,
  groups,
  initialValues,
  filter,
  emptyText = "暂无相关内容",
  cardLayout = "poster",
  gridClassName = "grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6",
  pageSize = 8,
}: CategoryBrowserProps) {
  const [values, setValues] = useState(initialValues);
  const [page, setPage] = useState(1);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const filtered = useMemo(() => filter(values), [filter, values]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleChange = (key: string, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleReset = () => {
    setValues(initialValues);
    setPage(1);
  };

  return (
    <div className="bg-background-50">
      <div className="mx-auto max-w-page px-4 md:px-6 py-8 md:py-12">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: crumbLabel }]} />

        <div className="mt-5 md:mt-6">
          <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 tracking-wide">
            {heading}
          </h1>
          <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-3xl leading-7">
            {description}
          </p>
        </div>

        <div className="mt-8 flex flex-col lg:flex-row lg:items-start gap-8">
          <FilterSidebar
            groups={groups}
            values={values}
            onChange={handleChange}
            onReset={handleReset}
            open={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
          />

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3 mb-5">
              <p className="text-sm text-foreground-600">
                共找到 <span className="font-medium text-primary-600">{filtered.length}</span> 条内容
              </p>
              <button
                type="button"
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden px-4 py-2 rounded-md border border-background-300 text-sm text-foreground-700 flex items-center gap-2 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-equalizer-line" />
                筛选
              </button>
            </div>

            {pageItems.length > 0 ? (
              <>
                <div className={cardLayout === "wide" ? "flex flex-col gap-4" : gridClassName}>
                  {pageItems.map((item) => (
                    <ContentCard key={item.id} item={item} layout={cardLayout} />
                  ))}
                </div>
                <div className="mt-10">
                  <Pagination
                    current={currentPage}
                    totalPages={totalPages}
                    onChange={setPage}
                  />
                </div>
              </>
            ) : (
              <EmptyState
                title={emptyText}
                description="换一个筛选条件试试，或者返回首页看看其他精彩内容。"
                actionLabel="重置筛选条件"
                onAction={handleReset}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}